import i18n from '@/i18n';
import type { VolumeManifestView } from '@/api/imaging.api';
import type { ChunkFetcher } from './volume3d';

/** Numele cache-ului din browser; alt nume = alt format, cel vechi se ignoră. */
export const VOLUME_CACHE = 'dentflow-volumes-v1';

/** Cât se folosește o bucată ținută minte; după, se cere din nou. */
export const VOLUME_CACHE_MAX_AGE_MS = 14 * 24 * 3600 * 1000;

/**
 * Amprenta unui volum pregătit: se schimbă când pregătirea îl reface altfel.
 *
 * Bucățile unei serii au aceeași cale după o pregătire din nou — și un conținut
 * nou, de pildă după ce ordinea feliilor a fost reparată. Cheia din cache poartă
 * deci amprenta manifestului (originea, direcția, valorile, mărimea fiecărei
 * bucăți), ca un volum refăcut să nu se citească din bucățile celui vechi.
 */
export function manifestVersion(manifest: VolumeManifestView): string {
  const text = JSON.stringify([
    manifest.origin,
    manifest.direction,
    manifest.min,
    manifest.max,
    manifest.levels.map((l) => [l.dims, l.spacing, l.chunks.map((c) => [c.z, c.slices, c.bytes])]),
  ]);
  // FNV-1a pe 32 de biți: destul pentru a deosebi două pregătiri ale aceleiași serii.
  let hash = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

interface CacheDeps {
  caches: CacheStorage | undefined;
  fetch: typeof fetch;
  now: () => number;
  origin: string;
  /** gzip → octeți; injectabil pentru teste. */
  gunzip: (gz: ArrayBuffer) => Promise<ArrayBuffer>;
}

const defaultDeps = (): CacheDeps => ({
  caches: typeof caches === 'undefined' ? undefined : caches,
  fetch: (...args) => fetch(...args),
  now: () => Date.now(),
  origin: globalThis.location?.origin ?? 'http://localhost',
  gunzip: async (gz) => {
    if (typeof DecompressionStream === 'undefined') {
      throw new Error(i18n.t('common:browserulAcestaNuPoateDezarhivaVolumul'));
    }
    return new Response(
      new Blob([gz]).stream().pipeThrough(new DecompressionStream('gzip')),
    ).arrayBuffer();
  },
});

/**
 * Bucățile unui volum, ținute minte în browser între deschideri.
 *
 * Linkurile semnate se schimbă la fiecare cerere, deci cache-ul HTTP al
 * browserului nu le recunoștea: un CBCT de 70–140 MB se descărca din nou la
 * fiecare deschidere a studiului. Aici cheia e calea bucății în depozit — partea
 * stabilă a linkului, fără semnătură — plus amprenta volumului.
 *
 * Sunt imagini ale pacienților, pe calculatorul clinicii: o bucată mai veche de
 * 14 zile nu se mai folosește, iar ieșirea din cont golește tot
 * (`clearVolumeCache`). Pe pagina partajată nu se folosește — acolo browserul e
 * al altcuiva. Fără Cache Storage (context nesigur, browser vechi), se descarcă
 * ca înainte.
 */
export function cachingFetcher(version: string, overrides: Partial<CacheDeps> = {}): ChunkFetcher {
  const deps = { ...defaultDeps(), ...overrides };
  return async (url, signal) => {
    const store = deps.caches ? await deps.caches.open(VOLUME_CACHE).catch(() => null) : null;
    const path = new URL(url, deps.origin).pathname;
    const key = `${deps.origin}/__volume-cache/${version}${path}`;

    if (store) {
      const hit = await store.match(key).catch(() => undefined);
      const at = Number(hit?.headers.get('x-cached-at') ?? 0);
      if (hit && deps.now() - at < VOLUME_CACHE_MAX_AGE_MS) {
        return deps.gunzip(await hit.arrayBuffer());
      }
      if (hit) await store.delete(key).catch(() => false);
    }

    const res = await deps.fetch(url, { signal });
    if (!res.ok) throw new Error(i18n.t('common:oBucataAVolumului', { status: res.status }));
    const gz = await res.arrayBuffer();
    if (store) {
      // Un cache plin sau refuzat nu oprește citirea: bucata se folosește oricum.
      await store
        .put(key, new Response(gz, { headers: { 'x-cached-at': String(deps.now()) } }))
        .catch(() => undefined);
    }
    return deps.gunzip(gz);
  };
}

/** Golește bucățile ținute minte — la ieșirea din cont. */
export async function clearVolumeCache(): Promise<void> {
  if (typeof caches === 'undefined') return;
  await caches.delete(VOLUME_CACHE).catch(() => false);
}
